import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CheckCircle as ApproveIcon, Cancel as DenyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { safetyGateService, SafetyGateStatus, type SafetyGateExecutionDto } from '../services/safetyGateService';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import { PageListLayout } from '../components/layouts/PageListLayout';

const statusColor = (status: SafetyGateStatus) => {
  switch (status) {
    case SafetyGateStatus.Pending:
      return 'warning' as const;
    case SafetyGateStatus.Approved:
      return 'success' as const;
    case SafetyGateStatus.Denied:
      return 'error' as const;
    case SafetyGateStatus.Timeout:
      return 'info' as const;
    default:
      return 'default' as const;
  }
};

/**
 * Approval queue page component.
 */
export const ApprovalQueuePage = () => {
  const { addNotification } = useNotificationStore();

  const [approvals, setApprovals] = useState<SafetyGateExecutionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<SafetyGateExecutionDto | null>(null);
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');

  const loadApprovals = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await safetyGateService.getPendingApprovals();
      setApprovals(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending approvals');
      addNotification('Failed to load pending approvals', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApprovals();
    // Refresh every 30 seconds
    const interval = setInterval(loadApprovals, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleApproveClick = (execution: SafetyGateExecutionDto) => {
    setSelectedExecution(execution);
    setComments('');
    setApproveDialogOpen(true);
  };

  const handleDenyClick = (execution: SafetyGateExecutionDto) => {
    setSelectedExecution(execution);
    setReason('');
    setDenyDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedExecution) return;

    try {
      await safetyGateService.approveSafetyGate(selectedExecution.id, { comments: comments || undefined });
      addNotification('Safety gate approved successfully', 'success');
      setApproveDialogOpen(false);
      setSelectedExecution(null);
      setComments('');
      loadApprovals();
    } catch (err: any) {
      addNotification(err.message || 'Failed to approve safety gate', 'error');
    }
  };

  const handleDeny = async () => {
    if (!selectedExecution || !reason.trim()) {
      addNotification('Reason is required for denial', 'error');
      return;
    }

    try {
      await safetyGateService.denySafetyGate(selectedExecution.id, { reason: reason.trim() });
      addNotification('Safety gate denied successfully', 'success');
      setDenyDialogOpen(false);
      setSelectedExecution(null);
      setReason('');
      loadApprovals();
    } catch (err: any) {
      addNotification(err.message || 'Failed to deny safety gate', 'error');
    }
  };

  return (
    <>
      <PageListLayout
        title="Approval Queue"
        headerActions={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadApprovals}
            disabled={loading}
          >
            Refresh
          </Button>
        }
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        loading={loading}
        isEmpty={approvals.length === 0}
        emptyState={{
          title: 'No pending approvals',
          description: 'All safety gate approvals have been processed.',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {approvals.map((approval) => (
            <Card key={approval.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {approval.safetyGateName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Action: {approval.actionName}
                    </Typography>
                    {approval.reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Reason: {approval.reason}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Evaluated: {new Date(approval.evaluatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <Chip label={approval.status} color={statusColor(approval.status)} size="small" />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={() => handleApproveClick(approval)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DenyIcon />}
                    onClick={() => handleDenyClick(approval)}
                  >
                    Deny
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </PageListLayout>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Safety Gate</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Comments (optional)"
              multiline
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny Dialog */}
      <Dialog open={denyDialogOpen} onClose={() => setDenyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deny Safety Gate</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeny} variant="contained" color="error" disabled={!reason.trim()}>
            Deny
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
